import _ from 'lodash';
import { Amount, PaymentStatusType } from '../../api';
import { parseAmount } from '../../services';
import { getPaymentIndex } from '../services';

export interface ClaimPaymentSummaryItem {
    id?: string;
    status?: PaymentStatusType | string;
    created?: string;
    payerControlNumber?: string;
    allowedAmount?: Amount;
    payerAmount?: Amount;
    chargeAmount?: Amount;
    patientAmount?: Amount;
}

export function getLatestPaymentsByPayerPriority<T extends ClaimPaymentSummaryItem>(payments: T[]): T[] {
    const primaries = _.filter(payments, payment => getPaymentIndex(payment.status as PaymentStatusType) === 0);
    const secondaries = _.filter(payments, payment => getPaymentIndex(payment.status as PaymentStatusType) === 1);
    const tertiaries = _.filter(payments, payment => getPaymentIndex(payment.status as PaymentStatusType) === 2);

    return tertiaries.length ? tertiaries : secondaries.length ? secondaries : primaries;
}

export function getPaymentsWithoutReversals<T extends ClaimPaymentSummaryItem>(payments: T[] = []): T[] {
    const getTimestamp = (created?: string): number => {
        if (!created) {
            return Number.POSITIVE_INFINITY;
        }

        const timestamp = new Date(created).getTime();
        return Number.isFinite(timestamp) ? timestamp : Number.POSITIVE_INFINITY;
    };

    const sortedPayments = _(payments)
        .slice()
        .sort((a, b) => {
            const aTs = getTimestamp(a.created);
            const bTs = getTimestamp(b.created);
            if (aTs === bTs) {
                return 0;
            }

            return aTs - bTs;
        })
        .value();

    const paymentReversals = sortedPayments.filter(
        payment => payment.status === PaymentStatusType.REVERSALOFPREVIOUSPAYMENT,
    );
    const nonReversalPayments = sortedPayments.filter(
        payment => payment.status !== PaymentStatusType.REVERSALOFPREVIOUSPAYMENT,
    );

    // Build stable FIFO queues per absolute charge amount from non-reversal payments.
    // This prevents index drift when multiple reversals share the same charge amount.
    const nonReversalQueuesByChargeAmount = new Map<string, T[]>();
    nonReversalPayments.forEach(payment => {
        const paymentChargeAmount = payment.chargeAmount;
        if (!paymentChargeAmount) {
            return;
        }

        const chargeAmountKey = Math.abs(parseAmount(paymentChargeAmount)).toString();
        const queue = nonReversalQueuesByChargeAmount.get(chargeAmountKey) || [];
        queue.push(payment);
        nonReversalQueuesByChargeAmount.set(chargeAmountKey, queue);
    });

    const reversedPayments = new Set<T>();

    paymentReversals.forEach(reversal => {
        let reversedPayment: T | undefined;
        const reversalControlNumber = reversal.payerControlNumber;
        if (reversalControlNumber) {
            reversedPayment = nonReversalPayments.find(payment => {
                if (reversedPayments.has(payment)) {
                    return false;
                }

                return payment.payerControlNumber === reversalControlNumber;
            });
        }

        if (reversedPayment) {
            reversedPayments.add(reversedPayment);
            return;
        }

        const chargeAmount = reversal.chargeAmount;
        if (!chargeAmount) {
            return;
        }

        const chargeAmountKey = Math.abs(parseAmount(chargeAmount)).toString();
        const queue = nonReversalQueuesByChargeAmount.get(chargeAmountKey);
        while (queue?.length && reversedPayments.has(queue[0])) {
            queue.shift();
        }

        reversedPayment = queue?.shift();
        if (reversedPayment) {
            reversedPayments.add(reversedPayment);
        }
    });

    return nonReversalPayments.filter(payment => !reversedPayments.has(payment));
}
